import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { FinancePracticeRevenueCard } from '../../types/financeDashboard.types'
import {
  getPracticeRevenue,
  type PeriodComparisonMetric,
  type PracticeRevenueSummary,
  getPracticeRevenueOverview,
} from '@services/api/dashboard/finance.api'
import type { ClinicGoal } from '@services/api/dashboard/clinic-goal.api'
import {
  buildComparisonFooter,
  computeChartAxis,
  filterYtdPeriodItems,
  formatCurrency,
  goalDollarsToChartUnits,
  formatGoalCurrencyLabel,
  formatHygieneVisitDate,
} from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import { useClinicGoal } from './useClinicGoal'

export type UsePracticeRevenueParams = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

function buildCardFooter(metric: PeriodComparisonMetric): {
  footerUp: boolean
  footerPct: string
  footerAmt: string
} {
  const { up } = buildComparisonFooter(metric, { isCurrency: true })
  const pct = Math.abs(metric.percentChange)
  const sign = metric.percentChange >= 0 ? '+' : '-'
  const footerPct = `${sign}${pct.toFixed(1)}%`
  const delta = metric.current - metric.previous
  const footerAmt = formatCurrency(Math.abs(delta), { compact: true })
  return { footerUp: up, footerPct, footerAmt }
}

function buildPracticeRevenueCard(
  leftLabel: string,
  metric: PeriodComparisonMetric,
  options?: { rightLabel?: string },
): FinancePracticeRevenueCard {
  const { footerUp, footerPct, footerAmt } = buildCardFooter(metric)
  return {
    leftLabel,
    value: formatCurrency(metric.current, { compact: true }),
    rightLabel: options?.rightLabel,
    footerUp,
    footerPct,
    footerAmt,
  }
}

const EMPTY_METRIC: PeriodComparisonMetric = {
  current: 0,
  previous: 0,
  percentChange: 0,
}

function buildCardsFromSummary(
  summary: PracticeRevenueSummary | undefined,
  clinicGoal: ClinicGoal | null,
): FinancePracticeRevenueCard[] {
  const grossRevenue = summary?.grossRevenue ?? EMPTY_METRIC
  const netCollection = summary?.netCollection ?? EMPTY_METRIC
  const adjustment = summary?.adjustment ?? EMPTY_METRIC
  const netProfit = summary?.netProfit ?? EMPTY_METRIC

  return [
    buildPracticeRevenueCard('Gross collection', grossRevenue, {
      rightLabel: formatGoalCurrencyLabel(
        clinicGoal?.practice_gross_collection_goal,
      ),
    }),
    buildPracticeRevenueCard('Net collection', netCollection, {
      rightLabel: formatGoalCurrencyLabel(
        clinicGoal?.practice_net_collection_goal,
      ),
    }),
    buildPracticeRevenueCard('Adjustments', adjustment),
    buildPracticeRevenueCard('Net profit', netProfit, {
      rightLabel: formatGoalCurrencyLabel(clinicGoal?.net_profit_goal),
    }),
  ]
}

export type UsePracticeRevenueResult = {
  range: RangeKey
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  hasData: boolean
  summary: PracticeRevenueSummary | undefined
  cards: FinancePracticeRevenueCard[]
  trendChart: {
    data: { label: string; Gross: number; Net: number }[]
    yDomain: [number, number]
    yTicks: number[]
    goalLine: number
  }
  adjustmentRows: {
    id: string
    date: string
    category: string
    amount: number
  }[]
}

function usePracticeRevenueFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UsePracticeRevenueParams & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getPracticeRevenue>>
  >
}): UsePracticeRevenueResult {
  const params = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(params, fetcher, {
    enabled,
    queued,
  })
  const { goal: clinicGoal } = useClinicGoal({ range, date, enabled, queued })

  const cards = useMemo(
    () => buildCardsFromSummary(data?.summary, clinicGoal),
    [data?.summary, clinicGoal],
  )

  const trendChart = useMemo(() => {
    const grossRaw = data?.grossCollectionPeriodSummary ?? []
    const netRaw = data?.netCollectionPeriodSummary ?? []
    const paired = filterYtdPeriodItems(
      range,
      date,
      grossRaw.map((gross, index) => ({ gross, net: netRaw[index] })),
      (item) => item.gross.label,
    )
    const chartData = paired.map(({ gross, net }) => ({
      label: gross.label,
      Gross: gross.value / 1000,
      Net: (net?.value ?? 0) / 1000,
    }))
    const allValues = chartData.flatMap((row) => [row.Gross, row.Net])
    const { yDomain, yTicks } = computeChartAxis(allValues)
    return {
      data: chartData,
      yDomain,
      yTicks,
      goalLine: goalDollarsToChartUnits(
        clinicGoal?.practice_revenue_trend_goal,
      ),
    }
  }, [
    data?.grossCollectionPeriodSummary,
    data?.netCollectionPeriodSummary,
    range,
    date,
    clinicGoal,
  ])

  const adjustmentRows = useMemo(() => {
    return (data?.adjustmentActivity ?? []).map((row, index) => ({
      id: `${row.date}-${row.category}-${index}`,
      date: formatHygieneVisitDate(row.date),
      category: row.category,
      amount: row.amount,
    }))
  }, [data?.adjustmentActivity])

  return {
    range,
    loading,
    error,
    hasPayload: data != null,
    hasData: hasNonZeroDashboardValues(
      data?.summary.grossRevenue.current,
      data?.summary.netCollection.current,
      data?.summary.adjustment.current,
      data?.summary.netProfit.current,
    ),
    summary: data?.summary,
    cards,
    trendChart,
    adjustmentRows,
  }
}

export function usePracticeRevenue(
  options: UsePracticeRevenueParams,
): UsePracticeRevenueResult {
  return usePracticeRevenueFromFetcher({
    ...options,
    fetcher: getPracticeRevenue,
  })
}

export function usePracticeRevenueOverview(
  options: UsePracticeRevenueParams,
): UsePracticeRevenueResult {
  return usePracticeRevenueFromFetcher({
    ...options,
    fetcher: getPracticeRevenueOverview,
  })
}
